/**
 * Récapitulatif des marqueurs, pour la vue d'ensemble.
 *
 * Même tableau que la section Marqueurs, en lecture seule et tronqué aux plus
 * récents : la question « ce qu'on a changé, ça a servi à quoi ? » se pose en
 * arrivant sur le dashboard, pas après un détour par un onglet de gestion.
 *
 * Tronqué, parce qu'une page d'accueil n'est pas un journal : trente marqueurs
 * y noieraient les graphiques qu'ils sont censés éclairer. Le lien de bas de
 * carte mène au reste.
 *
 * Absente quand aucun marqueur n'existe — et remplacée par une invitation à en
 * poser un, une fois seulement : un cadre vide sur la page d'accueil est du
 * bruit, mais une fonctionnalité que personne ne découvre l'est aussi.
 */

import { Link } from 'react-router-dom';

import { formatInteger } from '../lib/format';
import type { MarkerRecap } from '../hooks/useMarkerRecap';
import { Card } from './Card';
import { MarkerRecapTable } from './MarkerRecapTable';

/** Lignes affichées sur la vue d'ensemble, les plus récentes. */
const HOME_LIMIT = 6;

interface MarkerSummaryCardProps {
  recap: MarkerRecap;
  /** Atténue le tableau pendant qu'une nouvelle période charge. */
  isRefetching: boolean;
  /**
   * Query string à conserver dans les liens.
   *
   * La période vit dans l'URL : un lien nu ramènerait la section Marqueurs à
   * la période par défaut, et le verdict changerait sous les yeux du lecteur.
   */
  search: string;
}

export function MarkerSummaryCard({ recap, isRefetching, search }: MarkerSummaryCardProps) {
  if (recap.isPending) return null;

  if (recap.rows.length === 0) {
    return (
      <Card
        title="Marqueurs"
        subtitle="Aucun évènement daté pour l'instant"
        className="chart-grid__wide"
      >
        <p className="alert-empty">
          Les courbes disent <em>quand</em> quelque chose a changé, jamais <em>pourquoi</em>.{' '}
          <Link to={{ pathname: '/marqueurs', search }}>Posez un marqueur</Link> sur une sortie,
          un changement de tarif ou une campagne : un trait daté apparaîtra sur les courbes, avec
          le sens dans lequel les chiffres ont bougé autour de cette date.
        </p>
      </Card>
    );
  }

  return (
    <Card
      title="Effet des changements"
      subtitle="Avant/après sur 14 jours de chaque côté — un sens de variation, pas une preuve"
      className="chart-grid__wide"
    >
      <MarkerRecapTable
        rows={recap.rows}
        limit={HOME_LIMIT}
        isRefetching={isRefetching}
        renderOverflow={(hidden) => (
          <p className="marker-overflow">
            <Link to={{ pathname: '/marqueurs', search }}>
              {formatInteger(hidden)} marqueur{hidden > 1 ? 's' : ''} plus ancien
              {hidden > 1 ? 's' : ''} — voir la liste complète
            </Link>
          </p>
        )}
      />

      <p className="footnote">
        « — » signifie qu'il manque le recul pour conclure : il faut 5 jours mesurés de chaque
        côté du marqueur, dans la période affichée. Les colonnes « Comptes » et « Abonnés »
        comparent des <strong>pentes</strong> — le nombre gagné par jour — et non des totaux,
        qui monteraient de toute façon. Détail et gestion dans{' '}
        <Link to={{ pathname: '/marqueurs', search }}>la section Marqueurs</Link>.
      </p>
    </Card>
  );
}
